import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { IProduct, IProductFileWithFile } from "@evennt-tech/api-v2-core";
import { shallow } from "zustand/shallow";
import { unpackInlineProductConfigs } from "store/custom-product-option/utils/helpers";
import { ProductConfig } from "ts/api/product";
import { registerStore } from "store/utils/reset-store.utils";
import { collectProductFiles } from "tools/product/collectProductFiles";

type CustomProductOptionConfigsByProduct = Map<
  string,
  { configs: ProductConfig[]; files: IProductFileWithFile[] }
>;
export type ProductConfigsBySupplier = Map<
  string,
  CustomProductOptionConfigsByProduct
>;

export type State = {
  productConfigsBySupplier: ProductConfigsBySupplier;
};

type Action = {
  setFromProducts: (
    products: IProduct[],
    showId: string
  ) => ProductConfigsBySupplier;
  cacheRecommendedProducts: (
    product: IProduct,
    showId: string
  ) => ProductConfigsBySupplier;
  reset: () => void;
};

type CustomProductOptionShopStore = State & Action;

export const customProductOptionShopStore = create(
  devtools<CustomProductOptionShopStore>((set, get) => ({
    productConfigsBySupplier: new Map(),

    setFromProducts: (products, showId) => {
      const { productConfigsBySupplier } = get();
      const next: ProductConfigsBySupplier = new Map(productConfigsBySupplier);
      let hasUnpackedConfigs = false;

      products.forEach((product) => {
        if (!product?.supplierCompanyId) {
          return;
        }

        const configs = unpackInlineProductConfigs(
          product.customProductOptionConfigs,
          showId
        );

        if (!configs.length) {
          return;
        }

        const configsByProduct: CustomProductOptionConfigsByProduct = new Map(
          next.get(product.supplierCompanyId)
        );
        configsByProduct.set(product.id, {
          configs,
          files: collectProductFiles(product),
        });
        next.set(product.supplierCompanyId, configsByProduct);
        hasUnpackedConfigs = true;
      });

      if (!hasUnpackedConfigs) {
        return productConfigsBySupplier;
      }

      set({ productConfigsBySupplier: next });

      return next;
    },

    cacheRecommendedProducts: (product, showId) =>
      get().setFromProducts(
        (product.recommendedProducts ?? [])
          .map(({ recommendedProduct }) => recommendedProduct)
          .filter((recommended): recommended is IProduct => !!recommended),
        showId
      ),

    reset: () => {
      set({ productConfigsBySupplier: new Map() });
    },
  }))
);

export const useCustomProductOptionStore = <
  Return = Partial<CustomProductOptionShopStore>
>(
  selector: (state: CustomProductOptionShopStore) => Return
) => customProductOptionShopStore(selector, shallow);

registerStore(customProductOptionShopStore);
